<template>
  <card>
    <div class="sms-enable">
      <div class="sms-enable__left">
        <img src="/img/pandas/sms.png" alt="SMS" class="sms-enable__img" />
      </div>

      <div class="sms-enable__right">
        <h3 class="sms-enable__title text-white">{{ cancelledAt ? 'SMS is cancelled for' : 'Enable SMS for' }} {{ groupName || 'this location' }}</h3>

        <p class="sms-enable__lede">
          Send booking confirmations, reminders, waiver requests, and payment receipts by text from your own local number.
        </p>

        <div class="sms-enable__features">
          <div class="sms-enable__feature">
            <i class="tim-icons icon-send"></i>
            <span>Every message logged with its delivery status</span>
          </div>
          <div class="sms-enable__feature">
            <i class="tim-icons icon-settings-gear-63"></i>
            <span>Plug into your existing automations with a new "send SMS" step</span>
          </div>
          <div class="sms-enable__feature">
            <i class="tim-icons icon-badge"></i>
            <span>Your own local 10DLC number registered to your business</span>
          </div>
          <div class="sms-enable__feature">
            <i class="tim-icons icon-check-2"></i>
            <span>TCPA-compliant opt-in, opt-out, and audit trail built in</span>
          </div>
        </div>

        <div class="sms-enable__meta">
          <p class="sms-enable__meta-line">
            SMS costs $10 per month per phone number and includes 250 SMS credits. Additional credits cost 3¢ each.
            A one-time $20 activation fee covers carrier registration. Sent and received messages both use credits.
            These charges appear on your Off The Couch bill, separate from the monthly minimum.
          </p>
          <p class="sms-enable__meta-line">
            Initial setup takes a few minutes. Carrier approval of your business typically completes within 1 to 3 business days.
          </p>
        </div>

        <div class="sms-enable__cta">
          <info-hint v-if="cancelledAt" icon="icon-alert-circle-exc" variant="warning">
            SMS for this location was cancelled on {{ formattedCancelledAt }}. Contact support to enable it again.
          </info-hint>
          <base-button v-else type="primary" @click="$emit('start-onboarding')">
            Get started
          </base-button>
        </div>
      </div>
    </div>
  </card>
</template>

<script>
import { formattedDate } from '@/plugins/dateFormatter';

export default {
  name: 'SMSEnableCard',
  props: {
    groupName: {
      type: String,
      default: ''
    },
    // released_at of a cancelled group; when set the card explains instead of offering setup
    cancelledAt: {
      type: String,
      default: null
    }
  },
  computed: {
    formattedCancelledAt() {
      if (!this.cancelledAt) return '';
      try {
        const group = JSON.parse(localStorage.getItem('group') || '{}');
        return formattedDate(group.date_format || 'MM/DD/YYYY', this.cancelledAt);
      } catch (e) {
        return String(this.cancelledAt).slice(0, 10);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.sms-enable {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  padding: 1rem 1rem 1.5rem;

  @media (min-width: 992px) {
    grid-template-columns: minmax(240px, 340px) 1fr;
    gap: 3rem;
    padding: 1.5rem 1.5rem 2rem;
  }

  &__left {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__img {
    width: 100%;
    max-width: 280px;
    height: auto;
    opacity: 0.95;
  }

  &__right {
    display: flex;
    flex-direction: column;
  }

  &__title {
    margin: 0 0 0.5rem;
    font-size: 1.5rem;
  }

  &__lede {
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 1.5rem;
    line-height: 1.55;
  }

  &__features {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.625rem 1.25rem;
    margin: 0 0 1.5rem;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__feature {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
    line-height: 1.4;

    i {
      color: #e14eca;
      font-size: 1.1rem;
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  &__meta {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 1rem;
    margin: 0 0 1.5rem;
  }

  &__meta-line {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.82rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__cta {
    display: flex;
    justify-content: flex-start;
  }
}
</style>

<style>
/* Light mode - selectors namespaced under the component root so nothing leaks */
.white-content .sms-enable__lede { color: rgba(0, 0, 0, 0.65); }
.white-content .sms-enable__feature { color: rgba(0, 0, 0, 0.8); }
.white-content .sms-enable__meta { border-top-color: rgba(0, 0, 0, 0.1); }
.white-content .sms-enable__meta-line { color: rgba(0, 0, 0, 0.55); }
</style>
